/**
 * Popup JavaScript
 *
 * This file defines behaviors of
 * the Popup block.
 * 
 * Structure the popup's HTML like so (note
 * that the ID/link must start with "popup-")
 * 
	<a href="#popup-example">Activate Popup</a>
	<div class="popup js-popup" id="popup-example">
		<div class="popup__window">
			<button class="popup__close" type="button" aria-label="Close Popup"></button>
			<div class="popup__scroller">
				Popup's content here.
			</div>
		</div>
	</div>
 *
 */

/**
 * Import dependencies
 */
import onReady from '../../util/onReady';

/**
 * Handle popups
 */
const popups = () => {
	// prep to store current popup info
	let curPopup = null;
	let prevFocus = null;

	// toggle html5 video play if present
	const toggleVideo = (popup) => {
		const video = popup.querySelector('video');
		// eslint-disable-next-line no-unused-expressions
		video?.paused ? video?.play() : video?.pause();
	};

	// toggle iframes
	const toggleIframe = (popup) => {
		if (popup.classList.contains('popup--iframe')) {
			const iframe = popup.querySelector('iframe');
			if (!iframe.src) {
				iframe.setAttribute('src', iframe.dataset.src);
				iframe.addEventListener('load', () => {
					iframe.classList.remove('is-shown');
					setTimeout(() => {
						iframe.classList.add('is-shown');
					}, 100);
				});
			} else {
				setTimeout(() => {
					iframe.removeAttribute('src');
				}, 1000);
			}
		}
	};

	// close a popup
	const closePopup = () => {
		// hide the current popup
		curPopup.classList.remove('active');
		document.body.classList.remove('popup-active');
		// pause videos maybe and handle iframes
		toggleVideo(curPopup);
		toggleIframe(curPopup);
		// remove hash
		window.history.replaceState(null, null, ' ');
		// refocus on original target
		prevFocus?.focus();
		// reset activated popup
		curPopup = null;
		prevFocus = null;
	};

	// open a popup
	const openPopup = (popup, id, prev) => {
		// set current popup
		curPopup = popup;
		prevFocus = prev;
		// set the hash (so we can link to this popup)
		window.history.pushState(null, null, `#${id}`);
		// move this to the end of the body if we've not already
		if (!curPopup.classList.contains('popup--root')) {
			curPopup.classList.add('popup--root');
			curPopup.querySelector('.popup__frame');
			curPopup.setAttribute('tabindex', '0');
			document.body.appendChild(curPopup);
		}
		// play videos maybe and handle iframe
		toggleVideo(curPopup);
		toggleIframe(curPopup);
		// handle close
		if (!curPopup.closer) {
			curPopup.closer = curPopup.querySelector('.popup__close');
			curPopup.closer.addEventListener('click', closePopup);
		}
		// wait just a moment in case this just got moved (for animations)
		setTimeout(() => {
			// visually show the popup
			curPopup.classList.add('active');
			document.body.classList.add('popup-active');
			// focus on it for accessibility
			curPopup.focus();
		}, 10);
	};

	// create popup and return it
	const createPopup = (id) => {
		// create the elements
		const popup = document.createElement('div');
		popup.classList.add('popup', 'js-popup');
		popup.setAttribute('id', id);
		const wind = document.createElement('div');
		wind.classList.add('popup__window');
		const close = document.createElement('button');
		close.classList.add('popup__close', 'button--basic', 'button--plus');
		close.setAttribute('type', 'button');
		close.setAttribute('aria-label', 'Close Popup');
		const scroller = document.createElement('div');
		scroller.classList.add('popup__scroller');
		// put them together
		wind.append(close);
		wind.append(scroller);
		popup.append(wind);
		// send it!
		return popup;
	};

	// create an iframe
	const createIframe = (popup, src) => {
		const iframe = document.createElement('iframe');
		iframe.dataset.src = src;
		const scroller = popup.querySelector('.popup__scroller');
		scroller.append(iframe);
	};

	// handle popup open/close on click of document (delegated)
	const togglePopup = (e = null) => {
		// if don't have an active popup
		if (!curPopup) {
			// if a popup link got clicked
			const link = e.composedPath().find((node) => !!node.href);
			if (link && link.href.includes('#popup-')) {
				// get target ID
				const id = link.href.split('#')?.pop();
				// get target by ID
				let popup = document.getElementById(id);
				// maybe generate an iframe popup
				if (!popup && link.dataset.iframe) {
					popup = createPopup(id);
					popup.classList.add('popup--iframe');
					createIframe(popup, link.dataset.iframe);
				}
				// if we have a popup
				if (popup && popup.classList.contains('js-popup')) {
					// don't jump anywhere
					e.preventDefault();
					// open the popup
					openPopup(popup, id, link);
				}
			}
		} else if (e?.target?.classList.contains('js-popup')) {
			// close open popup on document click outside popup
			closePopup();
		}
	};

	document.addEventListener('click', togglePopup);

	// close popup on escape
	document.addEventListener('keyup', (e) => {
		if (curPopup && e.key === 'Escape') {
			closePopup();
		}
	});

	// handle popup activation on page load
	const handlePopupLoad = () => {
		// if we have a hash then open the popup
		if (window.location?.hash?.includes('#popup-')) {
			const id = window.location.hash.split('#').pop();
			// get target by ID
			const popup = document.getElementById(id);
			if (popup && popup.classList.contains('js-popup')) {
				// open the popup
				openPopup(popup, id);
			}
		}
	};

	onReady(handlePopupLoad);
};

popups();
